import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { graphql, Link } from 'gatsby';
import Layout from '../components/Layout';
import styled from '@emotion/styled';
import he from 'he';
import { Breakpoints } from '../components/Styles';
import RelatedPosts from '../components/RelatedPosts';
import SEO from '../components/Seo';

// Pulls out HTML tags from content that would be shown as part of
// snippets.
const HTML_TAG_REGEX = /(<([^>]+)>)/gi;

const BlogContentDiv = styled.div`
  figure {
    overflow-x: scroll;
    border-radius: 1rem;
    box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.2);
    background-image: linear-gradient(to right, white, white),
      linear-gradient(to right, white, white),
      linear-gradient(to right, rgba(0, 0, 0, 0.25), rgba(255, 255, 255, 0)),
      linear-gradient(to left, rgba(0, 0, 0, 0.25), rgba(255, 255, 255, 0));

    background-position: left center, right center, left center, right center;
    background-repeat: no-repeat;
    background-color: white;
    background-size: 20px 100%, 20px 100%, 10px 100%, 10px 100%;
    background-attachment: local, local, scroll, scroll;

    table {
      font-size: 0.85rem;
    }
  }

  @media (max-width: ${Breakpoints.lg}) {
    figure {
      margin: 0;
    }
  }
`;

export const BlogPostTemplate = ({
  content,
  categories,
  tags,
  title,
  date,
  author,
  relatedPosts,
}) => {
  return (
    <section className="section">
      <div className="container content">
        <div className="columns is-centered">
          <div className="column is-7">
            <h1
              className="title is-size-2 has-text-weight-bold is-bold-light"
              dangerouslySetInnerHTML={{
                __html: title,
              }}
            />
            <p>
              posted by <Link to={`/author/${author.slug}`}>{author.name}</Link>
            </p>

            <BlogContentDiv
              dangerouslySetInnerHTML={{ __html: content }}
              className="is-size-5"
            />
            <div className="mt-2">
              <small>
                <i>Last edited on: {date}</i>
              </small>
            </div>
            <div style={{ marginTop: `4rem` }}>
              {categories && categories.length ? (
                <div>
                  <h4>Categories</h4>
                  <ul className="taglist">
                    {categories.map(category => (
                      <li key={`${category.slug}cat`}>
                        <Link to={`/categories/${category.slug}/`}>
                          {category.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {tags && tags.length ? (
                <div>
                  <h4>Tags</h4>
                  <ul className="taglist">
                    {tags.map(tag => (
                      <li key={`${tag.slug}tag`}>
                        <Link to={`/tags/${tag.slug}/`}>{tag.name}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <RelatedPosts relatedPosts={relatedPosts} />
    </section>
  );
};

BlogPostTemplate.propTypes = {
  content: PropTypes.node.isRequired,
  title: PropTypes.string,
};

const BlogPost = ({ data }) => {
  const { wordpressPost: post } = data;
  const { allWordpressPost: relatedPosts } = data;

  return (
    <Layout>
      <SEO
        title={`${he.decode(post.title)} | Memm Blog`}
        excerpt={he.decode(post.excerpt.replace(HTML_TAG_REGEX, ''))}
        post={post}
      />
      <BlogPostTemplate
        content={post.content}
        categories={post.categories}
        tags={post.tags}
        title={post.title}
        date={post.date}
        author={post.author}
        relatedPosts={relatedPosts.edges}
      />
    </Layout>
  );
};

BlogPost.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.object,
  }),
};

export default BlogPost;

export const pageQuery = graphql`
  fragment PostFields on wordpress__POST {
    id
    slug
    content
    date(formatString: "MMMM DD, YYYY")
    title
  }
  query BlogPostByID($id: String!, $filter: wordpress__POSTFilterInput) {
    wordpressPost(id: { eq: $id }) {
      id
      title
      slug
      content
      excerpt
      date(formatString: "MMMM DD, YYYY")
      categories {
        name
        slug
      }
      tags {
        name
        slug
      }
      author {
        name
        slug
      }
    }

    allWordpressPost(
      limit: 3
      filter: $filter
      sort: { fields: date, order: DESC }
    ) {
      edges {
        node {
          excerpt
          id
          title
          date(formatString: "MMMM D, YYYY")
          slug
        }
      }
    }
  }
`;
